import {
  Box,
  CSSObject,
  Skeleton,
  Stack,
  styled,
  Typography,
  useTheme,
  useThemeProps,
} from '@mui/material';
import {
  FORM_FIELD_THEME_NAME,
  RICH_TEXT_FIELD_BOX_THEME_NAME,
} from '@onevision-resources/design-theme';
import {
  BubbleMenu,
  bubbleMenuMouseDownHandler,
  bubbleMenuShouldShow,
  EditorContent,
  getToolbarButtons,
  KEY_SHORTCUTS_MAC,
  KEY_SHORTCUTS_WINDOWS,
  SlashCommandMenu,
  useRichTextEditor,
} from '@provision/rich-text-editor';
import {
  DependencyList,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { BubbleMenuToolbarButton, EditorContainer } from 'src/components/RichTextEditor';
import {
  AddLinkPopover,
  useAddLinkPopover,
} from 'src/components/RichTextEditor/AddLinkPopover';
import { RichTextEditorProps } from 'src/components/RichTextEditor/rich-text-editor';
import { Platform } from 'src/utils/system';
import { ToolbarMenu } from './toolbar-menu';

type RichTextFieldProps = Partial<RichTextEditorProps> & {
  label?: string;
  loading?: boolean;
  dependencies?: DependencyList;
  className?: string;
  placeholder?: string;
  watchContent?: boolean;
  labelAbove?: boolean;
};

const StyledEditorContainer = styled(EditorContainer, {
  name: RICH_TEXT_FIELD_BOX_THEME_NAME,
  slot: 'Root',
  shouldForwardProp: (prop) =>
    prop !== 'hasPlaceholder' && prop !== 'error' && prop !== 'size',
})<{
  hasPlaceholder?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: RichTextEditorProps['size'];
}>(({ theme, hasPlaceholder, disabled, error }) => {
  let borderColor =
    theme.palette.mode === 'light'
      ? theme.palette.custom.OV.sys.outlineVariant
      : 'rgba(255, 255, 255, 0.23)';

  if (error) {
    borderColor = theme.palette.error.main;
  }

  const baseStyles = {
    ...(disabled
      ? { color: theme.palette.text.disabled }
      : {
          '&:hover': {
            borderColor: theme.palette.text.primary,
          },
        }),
    borderRadius: theme.spacing(1),
    border: '1px solid',
    borderColor,
    position: 'relative',

    '&:has(.ProseMirror:focus, .ProseMirror-focused)': {
      outline: '1px solid',
      outlineColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '.portal-editor': {
      fontSize: theme.utils.important('0.85rem'),
      padding: theme.spacing(1, 1.5),
      '--blockquote-border-color': theme.palette.custom.OV.sys.surfaceVariant,
      '--blockquote-background-color': theme.palette.custom.OV.sys.surface,
      '--placeholder-color': theme.palette.custom.OV.sys.outline,
      ...(error ? { '--placeholder-error-color': theme.palette.error.main } : {}),

      '& p': {
        margin: theme.spacing(0),
      },
    },
    '.portal-editor > *:first-of-type': {
      marginTop: theme.spacing(0.5),
    },
    '.portal-editor:focus-visible:has(.emptyEditor)': {
      overflow: 'visible',
    },
    '.emptyEditor': {
      position: 'relative',
    },
    '.emptyEditor::before': {
      color: theme.utils.important(theme.palette.text.secondary),
      position: 'absolute',
      transition: theme.transitions.create(['color', 'scale', 'translate'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
      zIndex: theme.zIndex.app.ServiceDeskConversations,
      height: theme.utils.important('auto'),
      maxWidth: theme.utils.important('100%'),
      overflowWrap: theme.utils.important('anywhere'),
      wordBreak: theme.utils.important('break-word'),
    },
    '.ProseMirror-focused .emptyEditor::before': {
      color: theme.utils.important(theme.palette.primary.main),
      backgroundColor:
        theme.palette.mode === 'light'
          ? theme.palette.custom.OV.sys.surfaceContainerLowest
          : '#414141',
      transformOrigin: 'top left',
      scale: '0.75',
      translate: '0 -90%',
      left: theme.spacing(-1.2),
      paddingInline: theme.spacing(1),
    },
  } as CSSObject;

  if (hasPlaceholder) {
    return {
      ...baseStyles,
      '.emptyEditor::before': {
        height: theme.utils.important('auto'),
        position: 'relative',
      },

      '.portal-editor:focus-visible:has(.emptyEditor)': {
        overflow: 'auto',
      },

      '.ProseMirror-focused .emptyEditor::before': {
        float: theme.utils.important('right'),
        width: '100%',
      },
    };
  }

  return baseStyles;
});

const StyledLabel = styled(Typography, {
  name: RICH_TEXT_FIELD_BOX_THEME_NAME,
  slot: 'Label',
})(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(0.5),
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(['color', 'scale', 'translate'], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.shorter,
  }),
  paddingInline: theme.spacing(1),
  height: 'min-content',
  margin: 0,
  backgroundColor:
    theme.palette.mode === 'light'
      ? theme.palette.custom.OV.sys.surfaceContainerLowest
      : '#414141',
  transformOrigin: 'top left',
  scale: '0.75',
  translate: '0 -50%',
  top: 0,
  zIndex: theme.zIndex.app.ServiceDeskConversations,

  '&:has(+ .portal-editor .emptyEditor)': {
    display: 'none',
  },

  '&:has(+ .ProseMirror-focused)': {
    color: theme.palette.primary.main,
  },
}));

export const RichTextField = (inProps: RichTextFieldProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    label,
    placeholder,
    editorOptions,
    loading,
    dependencies,
    editorContentProps,
    onBlur,
    error,
    onSubmit,
    className,
    watchContent,
    labelAbove = false,
    disabled,
    size,
    ...rest
  } = props;
  const labelId = useId();
  const hasLabelAbove = labelAbove && !!label;

  // State to force re-renders when editor state changes
  const [editorRevision, setEditorRevision] = useState(0);

  const onRichTextEditorSubmit = useCallback(
    (message: string) => onSubmit?.(message),
    [onSubmit],
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const openLinkPopoverRef = useRef<(selectedText: string) => void>(() => undefined);

  const slashCommandOptions = useMemo(
    () => ({
      helpers: {
        openFilePicker: handleAttachClick,
        openLinkPopover: (selectedText: string) => openLinkPopoverRef.current(selectedText),
      },
    }),
    [handleAttachClick],
  );

  const { editor, slashCommand } = useRichTextEditor({
    placeholderOptions: {
      placeholder: labelAbove ? placeholder : (label ?? placeholder),
      emptyEditorClass: 'emptyEditor',
    },
    className,
    onSubmit: onRichTextEditorSubmit,
    editorOptions: {
      onSelectionUpdate: () => {
        setEditorRevision((prev) => prev + 1);
      },
      onTransaction: () => {
        setEditorRevision((prev) => prev + 1);
      },
      ...editorOptions,
    },
    dependencies,
    watchContent,
    slashCommandOptions,
  });

  const theme = useTheme();
  const keyShortcuts = Platform.isMacOS ? KEY_SHORTCUTS_MAC : KEY_SHORTCUTS_WINDOWS;

  const {
    anchorEl: linkPopoverAnchorEl,
    selectedText,
    openPopover: handleAddLink,
    closePopover: handleLinkPopoverClose,
    handleLinkSubmit,
  } = useAddLinkPopover({ editor });

  useEffect(() => {
    openLinkPopoverRef.current = handleAddLink;
  }, [handleAddLink]);

  const bubbleRef = useRef<HTMLDivElement | null>(null);

  const { formatting: formattingButtons, other: otherButtons } = useMemo(() => {
    return getToolbarButtons({
      editor,
      keyShortcuts,
      onLinkClick: handleAddLink,
      onAttachClick: handleAttachClick,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, keyShortcuts, handleAddLink, handleAttachClick, editorRevision]);

  const toolbarButtons = useMemo(
    () => [...formattingButtons, ...otherButtons],
    [formattingButtons, otherButtons],
  );

  const onBlurCallback = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(e, editor);
    },
    [onBlur, editor],
  );

  if (loading) {
    return (
      <Skeleton
        width="100%"
        key={label}
        height={200}
        style={{
          marginTop: theme.spacing(-3),
        }}
      />
    );
  }

  return (
    !!editor && (
      <Stack>
        <AddLinkPopover
          open={!!linkPopoverAnchorEl}
          anchorEl={linkPopoverAnchorEl}
          onClose={handleLinkPopoverClose}
          onSubmit={handleLinkSubmit}
          initialTitle={selectedText}
        />
        <SlashCommandMenu
          open={slashCommand.state.isOpen}
          anchorEl={slashCommand.state.anchorEl}
          items={slashCommand.state.items}
          selectedIndex={slashCommand.state.selectedIndex}
          onSelect={slashCommand.selectItem}
          onHighlight={slashCommand.setSelectedIndex}
          onClose={slashCommand.close}
          theme={theme}
        />
        <BubbleMenu editor={editor} shouldShow={bubbleMenuShouldShow}>
          <Box
            ref={bubbleRef}
            onMouseDown={bubbleMenuMouseDownHandler}
            sx={{
              backgroundColor: theme.palette.custom.OV.sys.surface,
              padding: 1,
              boxShadow: theme.shadows[3],
            }}
          >
            {formattingButtons?.map((button) => (
              <BubbleMenuToolbarButton key={button.title} button={button} delay={1000} />
            ))}
          </Box>
        </BubbleMenu>
        {hasLabelAbove ? (
          <FormFieldLabel id={labelId} disabled={disabled}>
            {label}
          </FormFieldLabel>
        ) : null}
        <StyledEditorContainer
          {...rest}
          disabled={disabled}
          error={!!error}
          size={size}
          hasPlaceholder={!!placeholder || labelAbove}
        >
          <EditorContent
            {...(hasLabelAbove ? { role: 'group', 'aria-labelledby': labelId } : {})}
            {...editorContentProps}
            onBlur={onBlurCallback}
            editor={editor}
          >
            {!!label && !labelAbove && <StyledLabel variant="body2">{label}</StyledLabel>}
          </EditorContent>
        </StyledEditorContainer>
        {editorOptions?.editable !== false && (
          <ToolbarMenu
            toolbarButtons={toolbarButtons}
            editor={editor}
            fileInputRef={fileInputRef}
          />
        )}
      </Stack>
    )
  );
};
